import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import NexaApp from './nexa/NexaApp';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import DownloadApp from './pages/DownloadApp';
import HowItWorks from './pages/HowItWorks';
import LevantaStory from './pages/LevantaStory';
import Mission from './pages/Mission';
import Vision from './pages/Vision';
import WhoItsFor from './pages/WhoItsFor';
import Values from './pages/Values';
import Founder from './pages/Founder';
// Team page is temporarily disabled — keep the import commented out so
// the build doesn't pull in unused code. Uncomment along with the
// matching Route + nav links to re-enable in <1 min.
// import Team from './pages/Team';
import MediaPress from './pages/MediaPress';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import AdminPage from './pages/AdminPage';
import PartnersPage from './pages/PartnersPage';
import BlogIndex from './pages/blog/BlogIndex';
import BlogPost from './pages/blog/BlogPost';
import Community from './pages/Community';
import DailyPractice from './pages/DailyPractice';
import Results from './pages/Results';
import Affiliates from './pages/Affiliates';
import AffiliateWelcome from './pages/AffiliateWelcome';
import AffiliateTerms from './pages/AffiliateTerms';
import AffiliateLogin from './pages/AffiliateLogin';
import AffiliateDashboard from './pages/AffiliateDashboard';
import BringAFriend from './pages/BringAFriend';
import Videos from './pages/Videos';
import Home from './pages/HomeAlt2';
import NotFound from './pages/NotFound';
import Pitch from './pages/Pitch';
import Roadmap from './pages/Roadmap';
import OnePagerDownload from './pages/OnePagerDownload';
import BackgroundMusic from './components/BackgroundMusic';
import useAffiliateCookie from './hooks/useAffiliateCookie';

// Layout wrapper to conditionally show header/footer
const Layout = ({ children }) => {
  const location = useLocation();
  /* Capture `?ref=CODE` from any landing path and persist it as a
     90-day cookie so /download-app can show a gift banner later. Must
     run inside a <BrowserRouter> which Layout already is. */
  useAffiliateCookie();
  const isAdminPage = location.pathname === '/admin';
  // Pages that ship their own custom header (transparent, blends into the
  // hero gradient) and inline dark footer — hide the global Header / Footer
  // so they don't double up.
  const isHome = location.pathname === '/' || location.pathname === '';
  const isFaq = location.pathname === '/faq' || location.pathname === '/faq/';
  const isStory =
    location.pathname === '/levanta-story' ||
    location.pathname === '/levanta-story/' ||
    location.pathname === '/story' ||
    location.pathname === '/story/';
  const isFounder =
    location.pathname === '/founder' || location.pathname === '/founder/';
  const isMission =
    location.pathname === '/mission' || location.pathname === '/mission/';
  const isVision =
    location.pathname === '/vision' || location.pathname === '/vision/';
  // /team is temporarily disabled — uncomment to re-enable along with the
  // Route and menu links. Team.jsx is kept on disk for fast revival.
  // const isTeam =
  //   location.pathname === '/team' || location.pathname === '/team/';
  const isDownload =
    location.pathname === '/download-app' || location.pathname === '/download-app/';
  const isContact =
    location.pathname === '/contact' || location.pathname === '/contact/';
  const isMediaPress =
    location.pathname === '/media-press' || location.pathname === '/media-press/';
  const isHowItWorks =
    location.pathname === '/how-it-works' || location.pathname === '/how-it-works/';
  const isBlog = location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  const isPrivacy = location.pathname === '/privacy' || location.pathname === '/privacy/';
  const isTerms = location.pathname === '/terms' || location.pathname === '/terms/';
  const isPartners = location.pathname === '/partners' || location.pathname === '/partners/';
  const isAffiliate =
    location.pathname === '/affiliate' ||
    location.pathname === '/affiliate/' ||
    location.pathname === '/affiliates' ||
    location.pathname === '/affiliates/' ||
    location.pathname.startsWith('/affiliate/') ||
    location.pathname.startsWith('/affiliates/');
  const isBringAFriend =
    location.pathname === '/bring-a-friend' ||
    location.pathname === '/bring-a-friend/' ||
    location.pathname === '/brind-a-friend' ||
    location.pathname === '/brind-a-friend/' ||
    location.pathname === '/invite-a-friend' ||
    location.pathname === '/invite-a-friend/';
  const isPitch =
    location.pathname === '/pitch' ||
    location.pathname === '/pitch/' ||
    location.pathname === '/pitchnexa' ||
    location.pathname === '/pitchnexa/';
  const isOnePager =
    location.pathname === '/one-pager' || location.pathname === '/one-pager/';
  /* The catch-all `*` route renders <NotFound />, which already ships
     its own AltHeader + AltFooter. Detect it by ruling out all known
     legacy-chrome routes so the global Header/Footer don't double up. */
  const KNOWN_LEGACY_ROUTES = [
    '/community',
    '/daily-practice',
    '/results',
    '/values',
    '/who-its-for',
    '/videos',
    '/admin',
    '/new2',
    '/what-is-levanta',
  ];
  const isLegacyRoute = KNOWN_LEGACY_ROUTES.some(
    (p) => location.pathname === p || location.pathname === p + '/',
  );
  const isAltStyledRoute =
    isHome ||
    isFaq ||
    isStory ||
    isFounder ||
    isMission ||
    isVision ||
    // isTeam ||
    isDownload ||
    isContact ||
    isMediaPress ||
    isHowItWorks ||
    isBlog ||
    isPrivacy ||
    isTerms ||
    isPartners ||
    isAffiliate ||
    isBringAFriend ||
    isPitch ||
    isOnePager;
  /* If the path matches no known route, it's a 404 — and NotFound.jsx
     ships its own chrome, so suppress the legacy Header/Footer. */
  const isNotFound = !isAltStyledRoute && !isLegacyRoute && !isAdminPage;
  const hideChrome = isAdminPage || isAltStyledRoute || isNotFound;

  return (
    <>
      {!hideChrome && <Header />}
      {children}
      {!hideChrome && <Footer />}
    </>
  );
};

// Background music must never run inside the NEXA app — it hijacks the first
// tap (autoplay gesture listener) and blocks the iOS microphone session.
const MusicGate = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/nexa')) return null;
  return <BackgroundMusic />;
};

function App() {
  // nexa.levanta.life (or /nexa in preview) serves the standalone NEXA
  // assistant — its own product, no Levanta chrome or background music.
  if (
    window.location.hostname.startsWith('nexa.') ||
    window.location.pathname.startsWith('/nexa')
  ) {
    return <NexaApp />;
  }
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            {/* /new2 was the preview URL while the new homepage was being
                built — redirect to / so any in-the-wild links keep working
                without creating duplicate-content for SEO. */}
            <Route path="/new2" element={<Navigate to="/" replace />} />
            <Route path="/download-app" element={<DownloadApp />} />
            <Route path="/what-is-levanta" element={<Navigate to="/" replace />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/levanta-story" element={<LevantaStory />} />
            <Route path="/story" element={<LevantaStory />} />
            <Route path="/mission" element={<Mission />} />
            <Route path="/vision" element={<Vision />} />
            <Route path="/who-its-for" element={<WhoItsFor />} />
            <Route path="/values" element={<Values />} />
            <Route path="/founder" element={<Founder />} />
            {/* /team disabled — re-enable by uncommenting:
            <Route path="/team" element={<Team />} />
            */}
            <Route path="/media-press" element={<MediaPress />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/community" element={<Community />} />
            <Route path="/daily-practice" element={<DailyPractice />} />
            <Route path="/results" element={<Results />} />
            <Route path="/affiliates" element={<Affiliates />} />
            <Route path="/affiliate" element={<Affiliates />} />
            <Route path="/affiliate/welcome" element={<AffiliateWelcome />} />
            <Route path="/affiliate/terms" element={<AffiliateTerms />} />
            <Route path="/affiliates/login" element={<AffiliateLogin />} />
            <Route path="/affiliates/dashboard" element={<AffiliateDashboard />} />
            <Route path="/bring-a-friend" element={<BringAFriend />} />
            <Route path="/pitch" element={<Pitch />} />
            <Route path="/pitchnexa" element={<Pitch />} />
            {/* Unlisted, passcode-gated — intentionally absent from all menus,
                sitemap and prerender. Ships its own chrome. */}
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/one-pager" element={<OnePagerDownload />} />
            <Route path="/brind-a-friend" element={<BringAFriend />} />
            <Route path="/invite-a-friend" element={<BringAFriend />} />
            <Route path="/videos" element={<Videos />} />
            {/* NEXA — unlisted personal AI assistant (nexa.levanta.life). */}
            <Route path="/nexa" element={<NexaApp />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
        <MusicGate />
      </BrowserRouter>
    </div>
  );
}

export default App;
